import { useEffect, useState } from 'react';

import type { PageInfo } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { useT } from '@/i18n/LocaleProvider';

export type PageInfoProps = {
  lang: string;
  slug: string;
  onBack?: () => void;
};

const dateFormatter = new Intl.DateTimeFormat('ru', {
  dateStyle: 'long',
});

export function PageInfoView({ lang, slug, onBack }: PageInfoProps) {
  const t = useT();
  const [info, setInfo] = useState<PageInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    wikiApiClient
      .getPageInfo(lang, slug)
      .then((data: PageInfo) => {
        if (!cancelled) setInfo(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err, t('page_info_error')));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lang, slug, t]);

  return (
    <article className="note page-info" data-testid="page-info">
      <header className="note-header">
        <h1>{t('page_info_title')}</h1>
        {info ? <p className="note-kicker">{info.title}</p> : null}
      </header>

      {onBack ? (
        <button type="button" onClick={onBack}>
          {t('page_info_back')}
        </button>
      ) : null}

      {isLoading ? <div className="empty-note">{t('page_info_loading')}</div> : null}
      {!isLoading && error ? <div className="empty-note">{error}</div> : null}

      {!isLoading && !error && info ? (
        <dl className="page-info-list" data-testid="page-info-list">
          <div>
            <dt>{t('page_info_revisions')}</dt>
            <dd>{info.revision_count}</dd>
          </div>

          <div>
            <dt>{t('page_info_contributors')}</dt>
            <dd>
              {info.contributors.length > 0
                ? info.contributors.map((c) => c.name).join(', ')
                : t('page_info_none')}
            </dd>
          </div>

          <div>
            <dt>{t('page_info_inbound')}</dt>
            <dd>{info.inbound_count}</dd>
          </div>

          <div>
            <dt>{t('page_info_outbound')}</dt>
            <dd>{info.outbound_count}</dd>
          </div>

          <div>
            <dt>{t('page_info_categories')}</dt>
            <dd>{info.categories.length > 0 ? info.categories.join(', ') : t('page_info_none')}</dd>
          </div>

          <div>
            <dt>{t('page_info_current_revision')}</dt>
            <dd>{info.current_revision || t('page_info_none')}</dd>
          </div>

          <div>
            <dt>{t('page_info_created')}</dt>
            <dd>{formatDate(info.created_at)}</dd>
          </div>

          <div>
            <dt>{t('page_info_updated')}</dt>
            <dd>{formatDate(info.updated_at)}</dd>
          </div>

          {info.translations.length > 0 ? (
            <div>
              <dt>{t('page_info_translations')}</dt>
              <dd>{info.translations.map((tr) => `${tr.language}: ${tr.title}`).join(', ')}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </article>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return dateFormatter.format(date);
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
}
